import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "../Buttons/Button";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startAddApprovalReviewer } from "../../actions/approvalReview";
import {
  APPROVAL_EMAIL_INVALID,
  APPROVAL_EMAIL_SEND_BUTTON,
  APPROVAL_EMAIL_SEND_SENT,
  APPROVAL_REVIEWER_LABEL,
  APPROVAL_REVIEW_EMAIL_LABEL,
  APPROVAL_REVIEWER_EMAIL_DUPLICATED,
  APPROVAL_REVIEWER_MAX_REACHED,
} from "../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { REGEX_EMAIL } from "../../utils/regex";
import ContentApprovalReviewLink from "./ContentApprovalReviewLink";
import ApprovalReviewEmailSend from "./ApprovalReviewEmailSend";

const ApprovalReviewerRow = ({
  reviewerNumber = 1,
  approvalType = "content",
  approvalID = "",
  approvalUrl = "",
  reviewerID = "",
  isPrimary = false,
  showReviewerLabel = false,
  defaultEmail = "",
  isEmailSent = false,
  onReviewerEmailSent = () => {},
  onCopied = () => {},
  onReviewerAdded = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [localApprovalUrl, setLocalApprovalUrl] = useState(approvalUrl);
  const displayUrl = approvalUrl || localApprovalUrl;

  useEffect(() => {
    setLocalApprovalUrl(approvalUrl);
  }, [approvalUrl]);

  useEffect(() => {
    setEmail("");
    setIsSent(false);
    setValidationError("");
  }, [approvalID, approvalType, reviewerNumber]);

  const onSendAdditionalReviewerEmail = async () => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    if (!REGEX_EMAIL.test(normalizedEmail)) {
      setValidationError(t(APPROVAL_EMAIL_INVALID));
      return;
    }

    setValidationError("");
    setIsSending(true);

    const response = await dispatch(
      startAddApprovalReviewer({
        approvalType,
        approvalID,
        email: normalizedEmail,
      })
    );

    setIsSending(false);

    if (response?.ok) {
      setIsSent(true);
      setLocalApprovalUrl(response?.url || "");
      onReviewerAdded({
        reviewerID: response?.reviewerID || "",
        url: response?.url || "",
        email: normalizedEmail,
      });
      return;
    }

    if (response?.code === "maxReviewersReached") {
      setValidationError(t(APPROVAL_REVIEWER_MAX_REACHED));
      return;
    }

    if (response?.code === "reviewerEmailDuplicated") {
      setValidationError(t(APPROVAL_REVIEWER_EMAIL_DUPLICATED));
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {showReviewerLabel ? (
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            letterSpacing: "0.5px",
            color: "text.secondary",
            fontSize: "0.65rem",
          }}
        >
          {t(APPROVAL_REVIEWER_LABEL, { number: reviewerNumber })}
        </Typography>
      ) : null}

      {isPrimary ? (
        <ApprovalReviewEmailSend
          approvalType={approvalType}
          approvalID={approvalID}
          reviewerID={reviewerID}
          defaultEmail={defaultEmail}
          initialIsSent={isEmailSent}
          onCallbackEmailSent={onReviewerEmailSent}
        />
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "text.secondary",
                fontSize: "0.65rem",
              }}
            >
              {t(APPROVAL_REVIEW_EMAIL_LABEL)}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: 1.5,
              bgcolor: "background.paper",
              pl: 1.25,
              pr: 0.5,
              py: 0.5,
            }}
          >
            <TextField
              type="email"
              variant="standard"
              value={email}
              disabled={isSending || isSent}
              placeholder="email@ejemplo.com"
              onChange={(event) => {
                setEmail(event.target.value);
                if (validationError) {
                  setValidationError("");
                }
              }}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  border: 0,
                  boxShadow: "none",
                  bgcolor: "transparent",
                  minHeight: 32,
                  px: 0,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  py: 0.5,
                },
              }}
            />
            <Button
              type="button"
              color="secondary"
              loading={isSending}
              disabled={isSent || !email.trim()}
              onClick={onSendAdditionalReviewerEmail}
              sx={{
                minWidth: "max-content",
                height: 32,
                px: 1.5,
                flexShrink: 0,
                border: "none",
                boxShadow: "none",
              }}
            >
              {t(
                isSent ? APPROVAL_EMAIL_SEND_SENT : APPROVAL_EMAIL_SEND_BUTTON
              )}
            </Button>
          </Box>
          {validationError ? (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {validationError}
            </Typography>
          ) : null}
        </Box>
      )}

      {displayUrl ? (
        <ContentApprovalReviewLink
          approvalUrl={displayUrl}
          onCopied={onCopied}
        />
      ) : null}
    </Box>
  );
};

export default ApprovalReviewerRow;
